import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, FileCheck2, Link2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AionEmptyState } from "@/components/aion/AionEmptyState";
import { AionSectionCard } from "@/components/aion/AionSectionCard";

const STATUS_LABELS = {
  open: "Offen",
  validated: "Validiert",
  warning: "Warnung",
  needs_review: "Review nötig",
  archived: "Archiviert",
};

const decisionSearchText = (decision) => [
  decision?.title,
  decision?.summary,
  decision?.review_status,
  ...(decision?.assumptions || []),
  ...(decision?.success_criteria || []),
  ...(decision?.warning_signs || []),
  ...(decision?.evidence || []),
].filter(Boolean).join(" ").toLowerCase();

const formatDate = (value) => {
  if (!value) return "Kein Review-Termin";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

export const decisionReferences = (decision) => (decision?.linked_note_ids || []).map((id) => ({
  type: "Notiz",
  id,
}));

export const DecisionsView = ({
  activeProfile,
  decisions = [],
  focusDecisionId = null,
  onFocusConsumed,
  onOpenMemory,
}) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("attention");
  const readOnlySharedProfile = activeProfile?.access_mode === "shared_view";
  const [selectedDecisionId, setSelectedDecisionId] = useState(null);

  const counts = useMemo(() => ({
    all: decisions.length,
    attention: decisions.filter((item) => ["warning", "needs_review", "open"].includes(item?.review_status)).length,
    validated: decisions.filter((item) => item?.review_status === "validated").length,
    archived: decisions.filter((item) => item?.review_status === "archived").length,
  }), [decisions]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return decisions.filter((decision) => {
      if (filter === "attention" && !["warning", "needs_review", "open"].includes(decision?.review_status)) return false;
      if (filter === "validated" && decision?.review_status !== "validated") return false;
      if (filter === "archived" && decision?.review_status !== "archived") return false;
      return !needle || decisionSearchText(decision).includes(needle);
    });
  }, [decisions, filter, query]);

  useEffect(() => {
    if (!focusDecisionId) return;
    if (decisions.some((decision) => decision.id === focusDecisionId)) {
      setFilter("all");
      setQuery("");
      setSelectedDecisionId(focusDecisionId);
    }
    onFocusConsumed?.();
  }, [decisions, focusDecisionId, onFocusConsumed]);

  useEffect(() => {
    if (focusDecisionId) return;
    if (!filtered.length) {
      setSelectedDecisionId(null);
      return;
    }
    if (!filtered.some((decision) => decision.id === selectedDecisionId)) {
      setSelectedDecisionId(filtered[0]?.id || null);
    }
  }, [filtered, focusDecisionId, selectedDecisionId]);

  const selectedDecision = useMemo(
    () => filtered.find((decision) => decision.id === selectedDecisionId) || null,
    [filtered, selectedDecisionId],
  );

  const refs = decisionReferences(selectedDecision);

  return (
    <div className="aion-view aion-view-stack" data-testid="decisions-view">
      <AionSectionCard
        title="Entscheidungen"
        eyebrow="DECISION REVIEW"
        subtitle="AION zeigt Annahmen, Erfolgskriterien, Warnzeichen, Evidenz und den nächsten Review-Termin."
        action={!readOnlySharedProfile && onOpenMemory ? <Button size="sm" variant="outline" onClick={() => onOpenMemory()} data-testid="decision-open-memory">Memory öffnen</Button> : null}
        testId="decisions-hero-card"
      >
        <div className="aion-status-badge" data-testid="decisions-profile-badge">
          <span>Profil: {activeProfile?.name || "Ich"}</span>
          <small>{activeProfile?.access_mode === "shared_view" ? "Freigegebene Entscheidungen" : "Privater Entscheidungsbereich"}</small>
        </div>

        <div className="aion-inline-stats" data-testid="decision-counts">
          <div className="aion-inline-stat"><span>Gesamt</span><strong>{counts.all}</strong></div>
          <div className="aion-inline-stat"><span>Review</span><strong>{counts.attention}</strong></div>
          <div className="aion-inline-stat"><span>Validiert</span><strong>{counts.validated}</strong></div>
          <div className="aion-inline-stat"><span>Archiviert</span><strong>{counts.archived}</strong></div>
        </div>

        <div className="aion-search-row" data-testid="decision-search-row">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Entscheidungen durchsuchen"
            data-testid="decision-search-input"
          />
          <div className="aion-inline-actions" data-testid="decision-filter-actions">
            {[
              ["attention", "Review"],
              ["all", "Alle"],
              ["validated", "Validiert"],
              ["archived", "Archiv"],
            ].map(([id, label]) => (
              <button
                type="button"
                key={id}
                className={`aion-section-nav-item${filter === id ? " aion-section-nav-item-active" : ""}`}
                onClick={() => setFilter(id)}
                data-testid={`decision-filter-${id}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </AionSectionCard>

      <div className="aion-two-column" data-testid="decision-workspace">
        <AionSectionCard title="Review-Liste" eyebrow="ENTSCHEIDUNGEN" subtitle="Wähle eine Entscheidung für Details." testId="decision-list-card">
          {filtered.length ? (
            <div className="aion-memory-list" data-testid="decision-list">
              {filtered.map((decision, index) => {
                const active = decision.id === selectedDecisionId;
                const status = decision.review_status || "open";
                return (
                  <button
                    type="button"
                    key={decision.id || index}
                    className={`aion-memory-card${active ? " aion-memory-card-active" : ""}`}
                    aria-current={active ? "true" : undefined}
                    onClick={() => setSelectedDecisionId(decision.id)}
                    data-testid={`decision-item-${index}`}
                  >
                    <strong>{decision.title || `Entscheidung ${index + 1}`}</strong>
                    <small>{STATUS_LABELS[status] || status} · Review: {formatDate(decision.next_review_at)}</small>
                    <small>{decision.summary || "Keine Zusammenfassung hinterlegt."}</small>
                  </button>
                );
              })}
            </div>
          ) : (
            <AionEmptyState
              icon={FileCheck2}
              title="Keine passenden Entscheidungen."
              description="Entscheidungen aus AION Intelligence erscheinen hier automatisch."
              testId="decisions-empty"
            />
          )}
        </AionSectionCard>

        <AionSectionCard
          title={selectedDecision?.title || "Entscheidung öffnen"}
          eyebrow="REVIEW"
          subtitle={selectedDecision ? `${STATUS_LABELS[selectedDecision.review_status] || selectedDecision.review_status || "Offen"} · nächster Review ${formatDate(selectedDecision.next_review_at)}` : "Wähle links eine Entscheidung aus."}
          testId="decision-detail-card"
        >
          {selectedDecision ? (
            <div className="aion-view-stack" data-testid="decision-detail">
              <div className="aion-note" data-testid="decision-summary">
                <strong>Entscheidung</strong>
                <p>{selectedDecision.summary || "Keine Zusammenfassung hinterlegt."}</p>
              </div>

              <div className="aion-two-column">
                <div className="aion-note" data-testid="decision-assumptions">
                  <strong>Annahmen</strong>
                  {(selectedDecision.assumptions || []).length
                    ? (selectedDecision.assumptions || []).map((item, index) => <p key={`assumption-${index}`}>• {item}</p>)
                    : <p>Keine Annahmen hinterlegt.</p>}
                </div>
                <div className="aion-note" data-testid="decision-success-criteria">
                  <strong>Erfolgskriterien</strong>
                  {(selectedDecision.success_criteria || []).length
                    ? (selectedDecision.success_criteria || []).map((item, index) => <p key={`criterion-${index}`}>• {item}</p>)
                    : <p>Keine Erfolgskriterien hinterlegt.</p>}
                </div>
              </div>

              <div className="aion-two-column">
                <div className="aion-note" data-testid="decision-warning-signs">
                  <strong><AlertTriangle className="aion-button-icon" aria-hidden="true" /> Warnzeichen</strong>
                  {(selectedDecision.warning_signs || []).length
                    ? (selectedDecision.warning_signs || []).map((item, index) => <p key={`warning-${index}`}>• {item}</p>)
                    : <p>Keine Warnzeichen hinterlegt.</p>}
                </div>
                <div className="aion-note" data-testid="decision-evidence">
                  <strong><CheckCircle2 className="aion-button-icon" aria-hidden="true" /> Evidenz</strong>
                  {(selectedDecision.evidence || []).length
                    ? (selectedDecision.evidence || []).map((item, index) => <p key={`evidence-${index}`}>• {item}</p>)
                    : <p>Noch keine Evidenz hinterlegt.</p>}
                </div>
              </div>

              <div className="aion-note" data-testid="decision-references">
                <strong><Link2 className="aion-button-icon" aria-hidden="true" /> Verknüpfte Notizen</strong>
                {refs.length ? (
                  <div className="aion-memory-reference-list">
                    {refs.map((reference) => <span key={reference.id}>{readOnlySharedProfile ? reference.type : `${reference.type}: ${reference.id}`}</span>)}
                  </div>
                ) : (
                  <p>Keine verknüpften Notizen.</p>
                )}
              </div>

              {["warning", "needs_review"].includes(selectedDecision.review_status) ? (
                <div className="aion-error-box" data-testid="decision-attention-banner">
                  <ShieldAlert className="aion-list-icon" aria-hidden="true" />
                  Diese Entscheidung benötigt Aufmerksamkeit.
                </div>
              ) : null}
            </div>
          ) : (
            <AionEmptyState icon={FileCheck2} title="Wähle eine Entscheidung aus." description="Review-Metadaten und Referenzen erscheinen hier." testId="decision-detail-empty" />
          )}
        </AionSectionCard>
      </div>
    </div>
  );
};
